import classNames from 'classnames';
import React from 'react';

import {UI_REGION} from '@udemy/browse-utils';
import {LocalizedHtml} from '@udemy/i18n';
import {NoticeItem, PersonalPlanLandingBannerNoticeData} from '@udemy/notices';
import {
    interpolateSubscriptionNoticeData,
    PlanPeriodStyle,
    SubscriptionDiscountRibbon,
} from '@udemy/subscription-browse';
import {ConsumerSubscriptionPlan, SubscriptionPlanContext} from '@udemy/subscription-graphql';
import {useFormatCurrency, useUDData} from '@udemy/ud-data';

import {NoPlanAlertBanner} from 'src/features/personal-plan-landing-page/components/personal-plan-banner/no-plan-alert-banner.react-component';
import {
    useNonCatalogCheckoutSource,
    usePersonalPlanSubscriptionCheckoutEntry,
} from 'src/features/personal-plan-landing-page/personal-plan-subscription-checkout-entry-context';

import {PersonalPlanSubscriptionCTA} from '../personal-plan-subscription-cta/personal-plan-subscription-cta.react-component';
import styles from './personal-plan-hero-banner-content.module.less';

export interface PersonalPlanHeroBannerContentProps {
    notice?: NoticeItem<PersonalPlanLandingBannerNoticeData>;
    plan?: SubscriptionPlanContext<ConsumerSubscriptionPlan>;
    label: string;
    planLabelComponent?: React.ReactNode;
    fallbackTitle: string;
    fallbackSubtitle: string;
}

export const PersonalPlanHeroBannerContent = ({
    plan,
    notice: wrappedNotice,
    label,
    planLabelComponent,
    fallbackTitle,
    fallbackSubtitle,
}: PersonalPlanHeroBannerContentProps) => {
    let notice = wrappedNotice?.data;
    const subscriptionCheckoutEntry = usePersonalPlanSubscriptionCheckoutEntry();
    const {source: nonCatalogSource, noticeDataLoading} = useNonCatalogCheckoutSource();
    const currencyFormatter = useFormatCurrency();
    const {request} = useUDData();

    const isLoading = plan?.isLoading;
    const isSubscriber = plan && 'isSubscriber' in plan && plan.isSubscriber;

    const subscriptionPlan = plan && 'id' in plan ? plan : undefined;
    const subscriptionDiscount = subscriptionPlan?.selectedDiscount;
    if (subscriptionPlan && notice) {
        notice = interpolateSubscriptionNoticeData(
            subscriptionPlan,
            notice,
            currencyFormatter,
        ) as PersonalPlanLandingBannerNoticeData;
    }
    const showNoPlanAlert = !isLoading && !isSubscriber && !subscriptionPlan;

    return (
        <div className={styles['landing-banner-wrapper']}>
            {showNoPlanAlert && (
                <div className={styles['no-plan-alert']}>
                    <NoPlanAlertBanner label={label} />
                </div>
            )}

            <div className={styles['pp-banner-content']}>
                <div className={styles['hero-label']}>
                    {planLabelComponent ?? label}
                    {subscriptionDiscount && (
                        <SubscriptionDiscountRibbon
                            subscriptionDiscount={subscriptionDiscount}
                            className={styles['discount-ribbon']}
                        />
                    )}
                </div>
                <h1 className={styles['title']} data-purpose="title">
                    <LocalizedHtml html={notice?.title ?? fallbackTitle} interpolate={{}} />
                </h1>
                <div
                    className={classNames('ud-text-lg', styles['subtitle'])}
                    data-purpose="subtitle"
                >
                    {notice?.subtitle ?? fallbackSubtitle}
                </div>
                <PersonalPlanSubscriptionCTA
                    className={styles['subscription-cta']}
                    buttonClassName={styles['subscription-cta-button']}
                    subtitleClassName={notice?.tagline ? styles['tagline-hidden'] : ''}
                    plan={plan}
                    data-purpose="subscription-cta"
                    uiRegion={UI_REGION.PERSONAL_PLAN_BANNER}
                    planPeriodStyle={
                        request.isMobile
                            ? PlanPeriodStyle.MULTILINE_NO_MARGIN
                            : PlanPeriodStyle.SINGLE
                    }
                    noticeData={notice}
                    noticeDataLoading={noticeDataLoading}
                    source={nonCatalogSource}
                    subscriptionCheckoutEntry={subscriptionCheckoutEntry}
                />
                {notice?.tagline && <div className={styles['tagline-wide']}>{notice.tagline}</div>}
            </div>
        </div>
    );
};
